import { lazy, Suspense, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DisplayHeadline } from "../../../../components/DisplayHeadline";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { clearLcpPaint } from "../../../../lib/adoptLcpImage";
import { getProjectUrl } from "../../../../lib/portfolioRoutes";
import { WhatIDoBlock } from "../../../../components/ContentBlocks";
import { ProjectCardSlideshow } from "../../../../components/ProjectCardSlideshow/ProjectCardSlideshow";
import { ProjectStatusBadges } from "../../../../components/ProjectStatusBadges/ProjectStatusBadges";
import type { Project } from "../../../../lib/projects";
import { getFeaturedTestimonials } from "../../../../lib/testimonials";

function plainText(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

const StudioEfficiencyBento = lazy(() =>
  import("../../../../components/StoryElements/StudioEfficiencyBento").then((m) => ({
    default: m.StudioEfficiencyBento,
  })),
);
const PhilosophyBlock = lazy(() =>
  import("../../../../components/ContentBlocks").then((m) => ({
    default: m.PhilosophyBlock,
  })),
);

const projectStats = [
  { title: "Enterprise Clients", count: "50+" },
  { title: "Products Launched", count: "200+" },
  { title: "User Experience", count: "15+ Years" },
  { title: "Success Rate", count: "98%" },
];

const sectionPad = "px-4 sm:px-6 md:px-8";
const sectionContainer = `max-w-[1200px] mx-auto ${sectionPad}`;

export const MainContentSection = (): JSX.Element => {
  const navigate = useNavigate();
  const [portfolioItems, setPortfolioItems] = useState<Project[]>([]);
  const testimonials = getFeaturedTestimonials(2);

  useLayoutEffect(() => {
    clearLcpPaint();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void import("../../../../lib/projects").then((m) => {
      if (!cancelled) setPortfolioItems(m.getFeaturedProjects());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="flex flex-col items-start w-full">
      {/* Hero */}
      <div className="flex flex-col items-center justify-center w-full bg-gradient-to-br from-blue-50 to-white dark:from-slate-900 dark:to-gray-950 py-12 md:py-20">
        <div
          className={`${sectionContainer} flex flex-col lg:flex-row items-center gap-10 lg:gap-16 lg:justify-between w-full`}
        >
          <div className="flex flex-col w-full lg:max-w-[600px] items-start justify-center order-2 lg:order-1">
            <div className="mb-4">
              <span className="inline-block bg-blue-100 dark:bg-slate-800 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full text-sm font-semibold">
                Senior Product Designer
              </span>
            </div>

            <DisplayHeadline accent=" Drive Growth" className="mb-4 md:mb-6">
              Crafting Digital Products That
            </DisplayHeadline>

            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-6 md:mb-8 leading-relaxed">
              I help ambitious companies transform their ideas into successful digital products
              that users love and businesses thrive with.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-8 md:mb-12 w-full sm:w-auto">
              <Button
                className="w-full sm:w-auto bg-wwwuxmichalcomdodger-blue hover:bg-blue-600 transition-colors duration-200 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-lg shadow-lg hover:shadow-xl"
                onClick={() => navigate("/ux-portfolio")}
              >
                View Portfolio
              </Button>
              <Button
                className="w-full sm:w-auto bg-white dark:bg-gray-900 border-2 border-wwwuxmichalcomdodger-blue text-wwwuxmichalcomdodger-blue hover:bg-blue-50 dark:hover:bg-gray-800 transition-colors duration-200 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-lg"
                onClick={() => navigate("/contact")}
              >
                Contact Me
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:gap-8 w-full">
              {projectStats.map((stat) => (
                <div key={stat.title} className="flex flex-col">
                  <span className="text-2xl sm:text-3xl font-bold text-wwwuxmichalcomdodger-blue">
                    {stat.count}
                  </span>
                  <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{stat.title}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full max-w-[320px] sm:max-w-[400px] lg:max-w-[500px] relative flex-shrink-0 order-1 lg:order-2">
            <div className="absolute -top-6 -right-6 sm:-top-10 sm:-right-10 w-48 h-48 sm:w-80 sm:h-80 bg-blue-100 rounded-full opacity-20 pointer-events-none" />
            <div className="absolute -bottom-6 -left-6 sm:-bottom-10 sm:-left-10 w-36 h-36 sm:w-60 sm:h-60 bg-blue-200 rounded-full opacity-20 pointer-events-none" />
            <img
              src="/images/assets/uxmichal-your-ux-designer.svg"
              alt="UX Michal — your UX designer"
              className="relative z-10 w-full h-auto"
              width={470}
              height={802}
              decoding="async"
            />
          </div>
        </div>
      </div>

      <WhatIDoBlock />

      {/* Portfolio */}
      <div className="w-full bg-white dark:bg-gray-950 py-12 md:py-20">
        <div className={sectionContainer}>
          <div className="text-center mb-10 md:mb-16">
            <DisplayHeadline as="h2" size="section" align="center" className="mb-4">
              Featured Projects
            </DisplayHeadline>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              A selection of my most impactful work, delivering measurable results for ambitious
              clients.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {portfolioItems.map((item, index) => (
              <Card
                key={item.id}
                className="surface-card rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-200 md:hover:-translate-y-1 cursor-pointer"
                onClick={() => navigate(getProjectUrl(item))}
              >
                <div className="relative">
                  <ProjectCardSlideshow
                    project={item}
                    alt={plainText(item.title)}
                    className="w-full h-44 sm:h-48"
                    enableHoverSlideshow={false}
                    priority={index < 1}
                  />
                  <ProjectStatusBadges project={item} />
                  {item.year && (
                    <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300">
                      {item.year}
                    </div>
                  )}
                </div>
                <CardContent className="p-5 md:p-6">
                  <h3
                    className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2"
                    dangerouslySetInnerHTML={{ __html: item.title }}
                  />
                  <p
                    className="text-gray-600 dark:text-gray-400 line-clamp-2 text-sm md:text-base"
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10 md:mt-12">
            <Button
              className="w-full sm:w-auto bg-wwwuxmichalcomdodger-blue hover:bg-blue-600 transition-colors duration-200 text-white px-8 py-4 text-lg rounded-lg shadow-lg hover:shadow-xl"
              onClick={() => navigate("/ux-portfolio")}
            >
              View All Projects
            </Button>
          </div>
        </div>
      </div>

      <div className="w-full [content-visibility:auto] [contain-intrinsic-size:auto_600px]">
        <Suspense fallback={null}>
          <StudioEfficiencyBento />
        </Suspense>
      </div>

      <div className="w-full [content-visibility:auto] [contain-intrinsic-size:auto_400px]">
        <Suspense fallback={null}>
          <PhilosophyBlock />
        </Suspense>
      </div>

      {/* Testimonials */}
      <div className="w-full bg-white dark:bg-gray-950 py-12 md:py-20 [content-visibility:auto] [contain-intrinsic-size:auto_500px]">
        <div className={sectionContainer}>
          <div className="text-center mb-10 md:mb-16">
            <DisplayHeadline as="h2" size="section" align="center" className="mb-4">
              Client Success Stories
            </DisplayHeadline>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Hear from the companies I&apos;ve helped achieve their digital goals.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="surface-card border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm">
                <CardContent className="p-6 md:p-8 flex flex-col sm:flex-row gap-4 sm:gap-6">
                  {testimonial.image && (
                    <img
                      src={testimonial.image}
                      alt=""
                      className="w-16 h-16 rounded-full object-cover shrink-0"
                      loading="lazy"
                      decoding="async"
                      width={64}
                      height={64}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-gray-700 dark:text-gray-300 mb-4 italic text-sm md:text-base leading-relaxed">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                    <h3 className="font-bold text-gray-900 dark:text-gray-100 text-base">{testimonial.name}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
};
